import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import axios from "axios";

export const fetchFaultData = async (siteId, dateId) => {
    try {
        const faultCollectionRef = collection(
            db,
            "sites",
            siteId,
            "dates",
            dateId,
            "faults"
        );
        const faultSnapshot = await getDocs(faultCollectionRef);
        const faults = [];
        faultSnapshot.forEach((doc) => {
            faults.push({ id: doc.id, ...doc.data() });
        });
        return faults;
    } catch (error) {
        console.error(
            `Error fetching fault data for site ${siteId} and date ${dateId}:`,
            error
        );
        throw error;
    }
};

export const fetchSiteData = async (siteId) => {
    try {
        const siteDocRef = doc(db, "sites", siteId);
        const siteDoc = await getDoc(siteDocRef);

        if (!siteDoc.exists()) {
            throw new Error(`Site ${siteId} does not exist.`);
        }
        const siteData = siteDoc.data();
        const datesCollectionRef = collection(siteDocRef, "dates");
        const datesSnapshot = await getDocs(datesCollectionRef);
        const dates = [];
        const faultsByDate = {};
        const kwPerPanel = parseFloat(siteData.customFields?.panelProd || 0.0);
        let totalPanelsAffected = 0;
        let totalFaults = 0;
        const uniqueFaultIds = new Set();
        let faultLevels = { "Level 1": 0, "Level 2": 0, "Level 3": 0 };
        const inverterBlockData = {};

        for (const dateDoc of datesSnapshot.docs) {
            const dateId = dateDoc.id;
            const dateData = { id: dateId, ...dateDoc.data() };
            try {
                const faults = await fetchFaultData(siteId, dateId);
                dateData.faults = faults;
                faults.forEach((fault) => {
                    totalPanelsAffected += fault.panelCount || 0; 
                    totalFaults++;
                    if (!uniqueFaultIds.has(fault.ID)) {
                        uniqueFaultIds.add(fault.ID);
                    }
                    if (fault.level && faultLevels[fault.level] !== undefined) {
                        faultLevels[fault.level] += 1;
                    }
                    if (fault.location) {
                        const inverterBlock = fault.location.split('-')[0];   
                        if (!inverterBlockData[inverterBlock]) {
                            inverterBlockData[inverterBlock] = {
                                inverterBlock,
                                totalPanels: 0,
                                affectedPanels: 0,
                            };
                        }                       
                        inverterBlockData[inverterBlock].affectedPanels += fault.panelCount || 1;
                    }
                });
                totalPanelsAffected += faults.length;
                faultsByDate[dateId] = faults;
            } catch (error) {
                console.error(`Error fetching faults for date ${dateId}:`, error);
                dateData.faults = [];
            }
            dates.push(dateData);
        }
        const siteNotesCollectionRef = collection(siteDocRef, "SiteNotes");
        const notesSnapshot = await getDocs(siteNotesCollectionRef);
        const notes = [];
        notesSnapshot.forEach((doc) => {
            notes.push({ id: doc.id, ...doc.data() });
        });
        const polygons = siteData.polygons || {};
        const blockmap = polygons.blockmap || null;
        const inverterBlocks = polygons.IB || {};
        const overlays = siteData.overlays || {};
        let totalPanels = 0;
        const fetchPanelCount = async (geoJsonUrl) => {
            try {
                const response = await axios.get(geoJsonUrl);
                const geoJson = response.data;
                return geoJson.features?.length || 0;
            } catch (error) {
                console.error(`Error fetching GeoJSON from ${geoJsonUrl}:`, error);
                return 0;
            }
        };
        const panelCountPromises = Object.entries(inverterBlocks).map(async ([ib, geoJsonUrl]) => {
            const count = await fetchPanelCount(geoJsonUrl);
            totalPanels += count;
            if (!inverterBlockData[ib]) {
                inverterBlockData[ib] = { inverterBlock: ib, totalPanels: 0, affectedPanels: 0 };
            }
            inverterBlockData[ib].totalPanels = count; // ✅ Store the total panel count
        });
        await Promise.all(panelCountPromises);

        return {
            ...siteData,
            siteId,
            siteName: siteData.location?.name || "Unnamed Site",
            location: siteData.location,
            customFields: siteData.customFields,
            polygons: { blockmap, inverterBlocks },
            overlays,
            dates,
            notes,
            faultsByDate,
            totalPanels,
            totalPanelsAffected,
            totalFaults: uniqueFaultIds.size, 
            impactedKW: totalPanelsAffected * kwPerPanel,
            percentAffected: (totalPanelsAffected / totalPanels) * 100 || 0,
            faultLevels,
            inverterBlocks: inverterBlockData,
        };
    } catch (error) {
        console.error("Error fetching site data:", error);
        throw error;
    }
};
